import { useState, useEffect } from 'react';

import { ApolloError } from '@apollo/client';
import { Button } from '@netfront/ui-library';
import { IPdfUsage } from 'interfaces';
import { useRouter } from 'next/router';
import { last } from 'utils';

import { ReportTable } from '../ReportTable';

import { TABLE_COLUMNS_PDF } from './PdfTableView.constants';
import { getPdfUsageTableData } from './PdfTableView.helpers';
import { IPdfUsageTableData, PdfTableViewProps } from './PdfTableView.interfaces';

import { useExportPdfReport, useGetPdfUsage, useToast } from '../../../../../hooks';
import { REPORTING_PAGE_CONSTANTS } from '../../ReportingPage.constants';

const { pageSize: defaultPageSize, tableSelectionItems } = REPORTING_PAGE_CONSTANTS;

const PdfTableView = ({
  selectedTable,
  onTableChange,
  dateRange,
  onDateRangeChange,
  selectedView,
  onViewChange,
}: PdfTableViewProps) => {
  const { query } = useRouter();
  const { handleToastError } = useToast();

  const { projectId } = query;

  const [pdfUsageTableData, setPdfUsageTableData] = useState<IPdfUsageTableData[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [lastCursor, setLastCursor] = useState<string>();
  const [allPdfUsages, setAllPdfUsages] = useState<IPdfUsage[]>([]);
  const [isLoadMoreDisabled, setIsLoadMoreDisabled] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);


  const handleApolloError = (error?: ApolloError) => {
    if (!error) {
      return;
    }

    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleExportPdfReport, isLoading: isExportUsageReportLoading = false } = useExportPdfReport({
    fetchPolicy: 'no-cache',
    onCompleted: ({ report: { presignedUrl } }) => {
      window.open(String(presignedUrl), '_blank');
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const { handleGetPdfUsage, handleFetchMorePdfUsage, isLoading: isGetPdfUsageLoading = false } = useGetPdfUsage({
    fetchPolicy: 'no-cache',
    onCompleted: ({ edges, totalCount = 0 }) => {
      const lastEdge = last(edges);
      
      if (lastEdge && lastEdge.cursor !== lastCursor) {
        setLastCursor(lastEdge.cursor);
      }

      const pdfUsages = edges.map(({ node }) => node);
      setAllPdfUsages(pdfUsages);
      setIsLoadMoreDisabled(pdfUsages.length >= totalCount || totalCount <= pageSize);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });


  const handleChangeSearchValue = (value: string) => {
    setFilter(value);
  };

  const handleDownloadReport = () => {
    handleExportPdfReport({
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId),
      to: dateRange[1],
    });
  };

  const handleGeneratePdfReport = () => {
    handleGetPdfUsage({
      filter,
      first: pageSize,
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId),
      to: dateRange[1],
    });
  };

  const handlePageSizeChange = (changedPageSize: number) => {
    setPageSize(changedPageSize);
  };
  

  useEffect(() => {
    if (selectedTable !== tableSelectionItems[1].value) {
      return;
    }
    
    handleGeneratePdfReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedTable, pageSize, filter]);

  useEffect(() => {
    setPdfUsageTableData(
      getPdfUsageTableData({
        pdfUsages: allPdfUsages,
      }),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPdfUsages]);

  const isLoading = isExportUsageReportLoading || isGetPdfUsageLoading;

  return (
    <ReportTable<IPdfUsageTableData> 
      childrenEnd={<Button size="xs" text="Download" type="button" onClick={() => handleDownloadReport()} />}
      columns={TABLE_COLUMNS_PDF}
      data={pdfUsageTableData}
      dateRange={dateRange}
      emptyStateMessage="There are no pdf usage data available yet"
      handleOnPageSizeChange={handlePageSizeChange}
      handleOnPaginate={async () => {
        await handleFetchMorePdfUsage({
          after: lastCursor,
          filter,
          first: pageSize,
          from: dateRange[0],
          ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          projectId: String(projectId),
          to: dateRange[1],
        });
      }}
      handleSearch={handleChangeSearchValue}
      isLoading={isLoading}
      isLoadMoreDisabled={isLoadMoreDisabled}
      pageSize={pageSize}
      selectedTable={selectedTable}
      selectedView={selectedView}
      totalItems={totalItems}
      onDateRangeChange={onDateRangeChange}
      onTableChange={onTableChange}
      onViewChange={onViewChange}
    />
  );
};

export { PdfTableView };
